import {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { MASTER_DATA_FORM_FIELDS } from 'utils/constants'
import {
  convertTo12HourFormat,
  getDepartment,
  getFamily,
  getSection,
  toCategoryBreadcrumb,
} from 'utils/helpers'
const {
  ITEM_NUMBER,
  ACTIVE_ONLINE,
  BARCODE,
  MAIN_CATEGORY,
  DEPARTMENT,
  ERP_BRAND,
  ERP_ITEM_DESC,
  ERP_MARKETING_CODE,
  ERP_PACK_INFO,
  ERP_UOM,
  FAMILY,
  HIDE_AGGREGATORS,
  ACTIVATED_STORES,
  ONLINE_DESC,
  ONLINE_PACK_INFO,
  QTY_PER_UOM,
  SECTION,
  SUB_FAMILY,
  SAP_DEPARTMENT,
  SAP_SECTION,
  SAP_FAMILY,
  SAP_SUB_FAMILY,
  VAT_CLASS,
  VAT_PERCENTAGE,
  IS_AVAILABLE_IN_FRESH,
  PLU,
  CREATED_AT,
  UPDATED_AT,
  SAP_MATERIAL_NO,
  IS_BUNDLE_PRODUCT,
} = MASTER_DATA_FORM_FIELDS

export const columns: ColDef[] = [
  {
    headerName: ITEM_NUMBER.label,
    field: ITEM_NUMBER.key,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: SAP_MATERIAL_NO.label,
    field: SAP_MATERIAL_NO.key,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: BARCODE.label,
    field: '_id',
    minWidth: 150,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: DEPARTMENT.label,
    field: DEPARTMENT.key,
    valueFormatter: (params: ValueFormatterParams) => {
      return getDepartment(params.value)
    },
  },
  {
    headerName: SECTION.label,
    field: SECTION.key,
    valueFormatter: (params: ValueFormatterParams) => {
      return getSection(params.value)
    },
  },
  {
    headerName: FAMILY.label,
    field: FAMILY.key,
    valueFormatter: (params: ValueFormatterParams) => {
      return getFamily(params.value)
    },
  },
  {
    headerName: SUB_FAMILY.label,
    field: SUB_FAMILY.key,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: SAP_DEPARTMENT.label,
    field: SAP_DEPARTMENT.key,
    minWidth: 180,
    valueFormatter: (params: ValueFormatterParams) => {
      return getDepartment(params.value)
    },
  },
  {
    headerName: SAP_SECTION.label,
    field: SAP_SECTION.key,
    minWidth: 160,
    valueFormatter: (params: ValueFormatterParams) => {
      return getSection(params.value)
    },
  },
  {
    headerName: SAP_FAMILY.label,
    field: SAP_FAMILY.key,
    minWidth: 160,
    valueFormatter: (params: ValueFormatterParams) => {
      return getFamily(params.value)
    },
  },
  {
    headerName: SAP_SUB_FAMILY.label,
    field: SAP_SUB_FAMILY.key,
    minWidth: 170,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    // View-only — set only by the Catalogue Categories import. Rendered as an
    // "A > B > C" breadcrumb; cell clips and the full path shows on hover.
    headerName: MAIN_CATEGORY.label,
    field: MAIN_CATEGORY.key,
    minWidth: 220,
    cellRenderer: (params: ICellRendererParams) => {
      const label = toCategoryBreadcrumb(params.value)
      return label ? <span title={label}>{label}</span> : '-'
    },
  },
  {
    headerName: ERP_ITEM_DESC.label,
    field: ERP_ITEM_DESC.key,
    minWidth: 220,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: ERP_UOM.label,
    field: ERP_UOM.key,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: QTY_PER_UOM.label,
    field: QTY_PER_UOM.key,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: ERP_PACK_INFO.label,
    field: ERP_PACK_INFO.key,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: ERP_BRAND.label,
    field: ERP_BRAND.key,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: ERP_MARKETING_CODE.label,
    field: ERP_MARKETING_CODE.key,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: VAT_CLASS.label,
    field: VAT_CLASS.key,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: VAT_PERCENTAGE.label,
    field: VAT_PERCENTAGE.key,
    valueFormatter: (params: ValueFormatterParams) =>
      params.value !== undefined ? `${params.value}%` : '-',
  },
  {
    headerName: ONLINE_DESC.label,
    field: ONLINE_DESC.key,
    minWidth: 220,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: ONLINE_PACK_INFO.label,
    field: ONLINE_PACK_INFO.key,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: ACTIVE_ONLINE.label,
    field: ACTIVE_ONLINE.key,
    cellRenderer: (params: ICellRendererParams) => {
      return params?.value === true
        ? 'Yes'
        : params?.value === false
          ? 'No'
          : '-'
    },
  },
  {
    headerName: IS_AVAILABLE_IN_FRESH.label,
    field: IS_AVAILABLE_IN_FRESH.key,
    cellRenderer: (params: ICellRendererParams) => {
      return params?.value === true
        ? 'Yes'
        : params?.value === false
          ? 'No'
          : '-'
    },
  },
  {
    headerName: PLU.label,
    field: PLU.key,
    valueFormatter: (params: ValueFormatterParams) => params.value || '-',
  },
  {
    headerName: IS_BUNDLE_PRODUCT.label,
    field: IS_BUNDLE_PRODUCT.key,
    cellRenderer: (params: ICellRendererParams) => {
      return params?.value === true
        ? 'Yes'
        : params?.value === false
          ? 'No'
          : '-'
    },
  },
  {
    headerName: CREATED_AT.label,
    field: CREATED_AT.key,
    valueFormatter: (params: ValueFormatterParams) =>
      params.value ? convertTo12HourFormat(params.value) : '-',
  },
  {
    headerName: UPDATED_AT.label,
    field: UPDATED_AT.key,
    valueFormatter: (params: ValueFormatterParams) =>
      params.value ? convertTo12HourFormat(params.value) : '-',
  },
  {
    headerName: HIDE_AGGREGATORS.label,
    field: HIDE_AGGREGATORS.key,
    minWidth: 350,
    // set auto height
    wrapText: true,
    autoHeight: true,
    valueFormatter: (params) => {
      return params.value || ''
    },
    cellRenderer: (params: ICellRendererParams) => {
      const aggregators =
        params.value && params.value?.length > 0
          ? params.value.filter((agg: any) => agg.isActive === true)
          : []
      return (
        <>
          {aggregators && aggregators?.length > 0 ? (
            <div className="flex h-full w-full max-w-3xl flex-wrap gap-3 py-4">
              {aggregators?.map((aggregator: any) => (
                <span
                  className="min-w-[30%] rounded-md bg-slate-200/70 px-2 py-1 text-xs text-gray-800"
                  key={`${aggregator?._id}^${params.data?._id}`}
                >
                  {aggregator?.aggregatorName}
                </span>
              ))}
            </div>
          ) : (
            '-'
          )}
        </>
      )
    },
  },
  {
    headerName: ACTIVATED_STORES.label,
    field: ACTIVATED_STORES.key,
    minWidth: 350,
    // set auto height
    wrapText: true,
    autoHeight: true,
    valueFormatter: (params) => {
      return params.value || ''
    },
    cellRenderer: (params: ICellRendererParams) => {
      const stores: string[] = params?.value || []

      if (!stores || !stores.length) {
        return <span>-</span>
      }

      return (
        <div className="flex h-full w-full max-w-3xl flex-wrap gap-3 py-4">
          {stores.map((store) => (
            <span
              className="min-w-[30%] rounded-md bg-slate-200/70 px-2 py-1 text-xs text-gray-800"
              key={`${store}^${params.data?._id}`}
            >
              {store}
            </span>
          ))}
        </div>
      )
    },
  },
]
